"use client";

import type { VariantProps } from "class-variance-authority";

import { Dialog, DialogContent, DialogTrigger } from "@components/core/DialogWrapper";
import Play20 from "@ui/icons/20x20/play-20.svg";
import { useInView } from "@ui/lib/useInView";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import { useRef, useState } from "react";

import { formatDuration } from "../demos/STS/utils";
import LazyVideoPlayer from "./LazyVideoPlayer";

interface ModalVideoPlayerProps {
  className?: string;
  customTrigger?: React.ReactNode;
  hideDuration?: boolean;
  label?: string;
  labelSize?: "md" | "sm";
  previewSize?: "md" | "sm";
  video?: string;
}

const buttonVariants = cva(
  "effect-color effect-focus effect-zoom-small-hover bg-default group mx-auto flex items-center gap-12 border p-8 pr-20",
  {
    variants: {
      shape: {
        circle: "rounded-full",
        squared: "rounded-xl",
      },
    },
    defaultVariants: {
      shape: "circle",
    },
  },
);

const playButtonVariants = cva(
  "effect-color bg-inverse text-inverse group-hover:bg-darkest relative flex items-center justify-center overflow-hidden",
  {
    variants: {
      shape: {
        circle: "rounded-full",
        squared: "rounded-lg",
      },
      previewSize: {
        sm: "h-32 w-32",
        md: "h-52 w-52",
      },
    },
    compoundVariants: [
      {
        shape: "squared",
        previewSize: "sm",
        className: "w-48",
      },
      {
        shape: "squared",
        previewSize: "md",
        className: "w-72",
      },
    ],
    defaultVariants: {
      shape: "circle",
      previewSize: "md",
    },
  },
);

const videoBackgroundVariants = cva("absolute inset-0 overflow-hidden", {
  variants: {
    shape: {
      circle: "rounded-full",
      squared: "rounded-lg",
    },
  },
  defaultVariants: {
    shape: "circle",
  },
});

const ModalVideoPlayer = ({
  label,
  video,
  className,
  customTrigger,
  hideDuration,
  previewSize = "md",
  labelSize = "md",
  shape = "circle",
}: ModalVideoPlayerProps & VariantProps<typeof buttonVariants>) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [duration, setDuration] = useState<null | string>(null);

  const inView = useInView(buttonRef, { once: false });

  const handleMetadataLoaded = (event: { duration: number }) => {
    if (event.duration) {
      setDuration(formatDuration(event.duration));
    }
  };

  const defaultTrigger = (
    <button className={buttonVariants({ className, shape })} ref={buttonRef} type='button'>
      {/* Play button with video background */}
      <span className={playButtonVariants({ shape, previewSize })}>
        {/* Background video preview */}
        <div className={videoBackgroundVariants({ shape })}>
          <LazyVideoPlayer
            autoplay={inView}
            className='h-full w-full'
            clipEndTime={10}
            controls={false}
            fit='cover'
            loop
            muted
            onMetadataLoaded={handleMetadataLoaded}
            ratio='1x1'
            rounded={false}
            src={video ?? ""}
            withBorder={false}
            withPlayButton={false}
          />
        </div>
        <div className='absolute inset-0 bg-overlay-darkest' />
        {/* Play icon */}
        <Play20 className='effect-opacity relative z-20 opacity-80 group-hover:opacity-100' />
      </span>

      <div className='flex flex-col items-start gap-2'>
        <span className={labelSize === "sm" ? "f-ui-03" : "f-description-03"}>{label}</span>
        {!hideDuration && (
          <span className={cn("f-ui-04 text-light", !duration && "opacity-50")}>{duration ? duration : "00:00"}</span>
        )}
      </div>
    </button>
  );

  return (
    video && (
      <Dialog>
        <DialogTrigger asChild>{customTrigger || defaultTrigger}</DialogTrigger>
        <DialogContent className='aspect-16x9 max-h-[95vh] w-full max-w-[95vw] bg-none lg:max-h-[75vh] lg:max-w-[75vw]'>
          <LazyVideoPlayer autoplay controls muted={false} src={video} withBorder={false} />
        </DialogContent>
      </Dialog>
    )
  );
};

ModalVideoPlayer.displayName = "ModalVideoPlayer";

export { ModalVideoPlayer };
